import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { EventBus, MESSAGE_MAX_LENGTH, ATTACHMENT_ACCEPT, LIKE_REACTION, type ReplyPreviewDTO } from '@campus/core';
import { Button, Icon, Menu, MenuItem } from '../ds/index.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { useMembersStore, sessionInfo } from '../people/membersStore.js';
import { useCampusOnline } from '../people/presence.js';
import {
  useMessagingStore, openThread, loadOlder, setVisibleConversation, markRead, sendMessage, discardFailed,
  editMessage, deleteMessage, notifyTyping, addDraftFiles, retryVoice, toggleReaction, setReplyTarget, jumpToMessage, loadNewer,
  replyPreviewOf, type ThreadMessage,
} from '../../messaging/store.js';
import { EmojiPanel, MessageMenu, ReactionPills } from './Reactions.js';
import { MessageText, MentionSuggestions } from './Mentions.js';
import { isEmojiOnly } from '../../messaging/smileys.js';
import { matchMembers, mentionQueryAt } from '../../messaging/mentions.js';
import { replaceFinalSmiley, replaceTypedSmileys } from '../../messaging/smileys.js';
import type { MemberEntry } from '../people/membersStore.js';
import { VoicePlayer, VoiceRecorder, canRecordVoice } from './Voice.js';
import { DraftFiles, MessageAttachments } from './Attachments.js';
import { GifPanel, useGifsEnabled } from './Gifs.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { otherMemberId, useConversationTitle } from './ConversationList.js';
import { dayLabel, hourLabel, quoteText, sendErrorLabel, systemText } from './format.js';

/** Deux messages du même auteur à moins de 5 min : regroupés sous le même en-tête. */
const GROUP_GAP_MS = 5 * 60_000;
const TYPING_SEND_EVERY_MS = 2_000;

function TypingLine({ conversationId }: { conversationId: string }) {
  const typing = useMessagingStore((s) => s.typing[conversationId]);
  const members = useMembersStore((s) => s.members);
  const [, tick] = useState(0);
  const now = Date.now();
  const names = Object.entries(typing ?? {})
    .filter(([, until]) => until > now)
    .map(([userId]) => members.get(userId)?.displayName ?? 'Quelqu’un');
  useEffect(() => {
    if (names.length === 0) return;
    const t = setTimeout(() => tick((n) => n + 1), 1000);
    return () => clearTimeout(t);
  });
  if (names.length === 0) return null;
  return (
    <p aria-live="polite" className="pl-msg-meta" style={{ margin: '0 16px 6px' }}>
      {names.length === 1 ? `${names[0]} écrit…` : `${names.join(', ')} écrivent…`}
    </p>
  );
}

/** Nom de l'auteur d'un message cité : « Toi », le nom de l'annuaire, sinon celui du message. */
function useQuoteName(r: ReplyPreviewDTO): string {
  const member = useMembersStore((s) => s.members.get(r.senderId));
  if (r.senderId && r.senderId === sessionInfo().userId) return 'Toi';
  return member?.displayName ?? (r.senderName || 'Membre');
}

/** Message cité, au-dessus d'une réponse : clic = remonter jusqu'à lui. */
function QuoteBlock({ reply, onJump }: { reply: ReplyPreviewDTO; onJump: () => void }) {
  const name = useQuoteName(reply);
  const unavailable = reply.deleted && !reply.senderId; // masqué par la modération
  return (
    <button type="button" className="pl-msg-quote" disabled={unavailable}
      title={unavailable ? undefined : 'Voir le message'} onClick={(e) => { e.stopPropagation(); onJump(); }}>
      {!unavailable && <span className="pl-msg-quote-name">{name}</span>}
      <span className={`pl-msg-quote-text${reply.deleted ? ' is-deleted' : ''}`}>{quoteText(reply)}</span>
    </button>
  );
}

/** Bandeau « Répondre à … » au-dessus de la saisie. */
function ReplyBanner({ conversationId, target }: { conversationId: string; target: ThreadMessage }) {
  const preview = replyPreviewOf(target);
  const name = useQuoteName(preview);
  return (
    <div className="pl-reply-banner" role="status">
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span className="pl-msg-quote-name">Répondre à {name}</span>
        <span className="pl-msg-quote-text">{quoteText(preview)}</span>
      </span>
      <Button size="sm" variant="ghost" icon="x" aria-label="Annuler la réponse" title="Annuler (Échap)"
        onClick={() => setReplyTarget(conversationId, null)} />
    </div>
  );
}

function DayDivider({ day }: { day: string }) {
  return (
    <div aria-hidden="true" className="pl-day" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 4px', font: '700 11px/1 var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--color-border-default)' }} />{day}<span style={{ flex: 1, height: 1, background: 'var(--color-border-default)' }} />
    </div>
  );
}

/** Message système d'un groupe (« Inès a ajouté Paul »), centré, sans actions. */
function SystemLine({ message }: { message: ThreadMessage }) {
  useMembersStore((s) => s.members); // noms de l'annuaire
  return (
    <p data-msg-id={message.id} className="pl-msg-system" style={{ marginTop: 10 }}>
      {systemText(message, message.system!)}
      <span className="pl-msg-meta"> · {hourLabel(message.createdAt)}</span>
    </p>
  );
}

function MessageItem({ message, showHeader, status, editing, onStartEdit, onStopEdit, selected, onSelect, onReply, onJump, flash }: {
  message: ThreadMessage;
  showHeader: boolean;
  /** Sous son dernier message : « Envoi… », « Envoyé », « Reçu » ou « Vu » (ces deux-là en privé). */
  status: string | null;
  editing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  selected: boolean;
  onSelect: () => void;
  onReply: () => void;
  onJump: (messageId: string) => void;
  /** Surlignage bref après un saut depuis une citation. */
  flash: boolean;
}) {
  const { userId: me } = sessionInfo();
  const mine = message.senderId === me;
  const member = useMembersStore((s) => s.members.get(message.senderId));
  const name = mine ? 'Toi' : (member?.displayName ?? message.senderName ?? 'Membre');
  const [draft, setDraft] = useState(message.text);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAct = mine && !message.deletedAt && !message.pending;
  const canReact = !message.deletedAt && !message.pending;
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { if (editing) { setDraft(message.text); setError(null); } }, [editing, message.text]);

  const save = async () => {
    const text = draft.trim();
    if (!text) return;
    if (text === message.text) { onStopEdit(); return; }
    try { await editMessage(message, text); onStopEdit(); } catch { setError('La modification n’est pas passée. Réessaie.'); }
  };

  const remove = async () => {
    try { await deleteMessage(message); setConfirmDelete(false); } catch { setError('La suppression n’est pas passée. Réessaie.'); }
  };

  const onEditKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onStopEdit(); }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void save(); }
  };

  // Double-clic sur le message = pouce levé (le « like » rapide), jamais sur un bouton, un lecteur ou la saisie.
  const onDoubleClick = (e: MouseEvent) => {
    if (!canReact || editing) return;
    if ((e.target as HTMLElement).closest('button, a, input, textarea, audio, video, img, [role="slider"], .pl-msg-more, .pl-msg-menu')) return;
    window.getSelection()?.removeAllRanges();
    void toggleReaction(message, LIKE_REACTION, true);
  };

  return (
    <div
      data-msg-id={message.id}
      className={`pl-msg${selected || menuOpen ? ' is-selected' : ''}${flash ? ' is-flash' : ''}`}
      style={{ display: 'flex', gap: 10, marginTop: showHeader ? 18 : 4 }}
      onClick={() => { if (canReact && !editing) onSelect(); }}
      onDoubleClick={onDoubleClick}
    >
      <div style={{ width: 32, flexShrink: 0 }}>
        {showHeader && <PersonAvatar userId={message.senderId} name={name} size={32} highlight={mine} />}
      </div>
      {/* Largeur du contenu (pas de tout le panneau) : la flèche d'actions se pose juste à droite du message. */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: '0 1 auto', maxWidth: 'calc(100% - 72px)' }}>
        {canReact && !editing && (
          <button type="button" className="pl-msg-more" style={{ top: showHeader ? 26 : 4 }} aria-label="Réagir, répondre…" title="Réagir, répondre…"
            aria-haspopup="menu" aria-expanded={menuOpen} onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}>
            <Icon name="chevron-down" size={16} />
          </button>
        )}
        {showHeader && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: mine ? 'var(--color-text-primary)' : 'var(--color-primary-text)' }}>{name}</span>
            <span className="pl-msg-meta">{hourLabel(message.createdAt)}</span>
          </div>
        )}
        {message.replyTo && !message.deletedAt && <QuoteBlock reply={message.replyTo} onJump={() => onJump(message.replyTo!.id)} />}
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <textarea
              className="pl-input pl-composer" aria-label="Modifier le message" autoFocus rows={2}
              value={draft} maxLength={MESSAGE_MAX_LENGTH} onChange={(e) => setDraft(e.target.value)} onKeyDown={onEditKey}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Button size="sm" variant="primary" onClick={() => { void save(); }} disabled={!draft.trim()}>Enregistrer</Button>
              <Button size="sm" variant="ghost" onClick={onStopEdit}>Annuler</Button>
              <span className="pl-msg-meta">Entrée pour enregistrer, Échap pour annuler</span>
            </div>
          </div>
        ) : message.deletedAt ? (
          <p className="pl-msg-text" style={{ fontStyle: 'italic', color: 'var(--color-text-muted)' }}>Message supprimé</p>
        ) : message.text ? (
          <p className={`pl-msg-text pl-msg-bubble${mine ? ' is-mine' : ''}${isEmojiOnly(message.text) ? ' pl-msg-emoji-only' : ''}`} style={{ opacity: message.pending === 'sending' ? 0.6 : 1 }}>
            <MessageText text={message.text} />
            {message.editedAt && <span className="pl-msg-meta"> (modifié)</span>}
          </p>
        ) : null}
        {!message.deletedAt && message.voiceDraft && message.attachments.length === 0 ? (
          <div style={{ opacity: 0.6, marginTop: 4 }}>
            <VoicePlayer load={async () => message.voiceDraft!.url} durationMs={message.voiceDraft.durationMs} waveform={message.voiceDraft.waveform} />
          </div>
        ) : !message.deletedAt && <MessageAttachments attachments={message.attachments ?? []} dimmed={message.pending === 'sending'} />}
        <ReactionPills message={message} />
        {message.pending === 'failed' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span role="alert" className="pl-msg-meta" style={{ color: 'var(--color-danger)' }}>{sendErrorLabel(message.error)}</span>
            <Button size="sm" variant="ghost" onClick={() => {
              if (message.voiceDraft) retryVoice(message); else void sendMessage(message.conversationId, message.text, message);
            }}>Réessayer</Button>
            <Button size="sm" variant="ghost" onClick={() => discardFailed(message.conversationId, message.id)}>Retirer</Button>
          </div>
        )}
        {confirmDelete && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <span className="pl-msg-meta">Supprimer ce message pour tout le monde ?</span>
            <Button size="sm" variant="danger" onClick={() => { void remove(); }}>Supprimer</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>Annuler</Button>
          </div>
        )}
        {error && <span role="alert" className="pl-msg-meta" style={{ color: 'var(--color-danger)' }}>{error}</span>}
        {status && <span className="pl-msg-meta" aria-live="polite">{status}</span>}
      </div>
      {menuOpen && (
        <MessageMenu message={message} canReact={canReact} canAct={canAct} onReply={onReply}
          onEdit={onStartEdit} onDelete={() => setConfirmDelete(true)} onClose={() => setMenuOpen(false)} />
      )}
    </div>
  );
}

/** Une conversation : fil (anciens messages en remontant), saisie, modifier/supprimer ses messages. */
export function ConversationView({ conversationId, onBack, onInfo }: {
  conversationId: string;
  onBack?: () => void;
  /** Groupe : ouvrir sa fiche (membres, admins, quitter). */
  onInfo?: () => void;
}) {
  const conv = useMessagingStore((s) => s.conversations.find((c) => c.id === conversationId));
  const thread = useMessagingStore((s) => s.threads[conversationId]);
  const status = useMessagingStore((s) => s.status);
  const title = useConversationTitle(conv);
  const otherId = conv?.kind === 'direct' ? otherMemberId(conv) : null;
  const online = useCampusOnline();
  const { userId: me } = sessionInfo();

  const [input, setInput] = useState('');
  /** Mention en cours de frappe (« @Jul… ») et suggestion surlignée. */
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const members = useMembersStore((s) => s.members);
  const [dragging, setDragging] = useState(false);
  const [recording, setRecording] = useState(false);
  // « + » de la saisie : fichier, emoji, GIF regroupés pour laisser la place au texte.
  const [extra, setExtra] = useState<null | 'menu' | 'emoji' | 'gif'>(null);
  const isMobile = useIsMobile();
  const fileRef = useRef<HTMLInputElement>(null);
  const drafts = useMessagingStore((s) => s.drafts[conversationId]);
  const canAttach = !sessionInfo().isGuest;
  const gifsEnabled = useGifsEnabled() && canAttach;
  const uploading = (drafts ?? []).some((d) => d.status === 'uploading');
  const readyFiles = (drafts ?? []).filter((d) => d.status === 'ready').length;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const replyTarget = useMessagingStore((s) => s.replyTo[conversationId]);
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Messages ajoutés en bas d'un fil détaché : garder la place, ne pas sauter en bas. */
  const keepScrollRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const atBottomRef = useRef(true);
  const restoreFromBottomRef = useRef<number | null>(null);
  const lastTypingRef = useRef(0);

  useEffect(() => {
    void openThread(conversationId);
    setVisibleConversation(conversationId);
    const onVisible = () => { if (document.visibilityState === 'visible') void markRead(conversationId); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      setVisibleConversation(null);
    };
  }, [conversationId]);

  const messages = thread?.messages ?? [];

  // Reste en bas quand on y était ; garde sa place quand des anciens messages arrivent au-dessus.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (keepScrollRef.current) {
      keepScrollRef.current = false;
    } else if (restoreFromBottomRef.current !== null) {
      el.scrollTop = el.scrollHeight - restoreFromBottomRef.current;
      restoreFromBottomRef.current = null;
    } else if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (el.scrollTop < 80 && thread?.olderCursor && !thread.loadingOlder) {
      restoreFromBottomRef.current = el.scrollHeight - el.scrollTop;
      void loadOlder(conversationId);
    }
    if (thread?.newerCursor && !thread.loadingNewer && el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      keepScrollRef.current = true;
      void loadNewer(conversationId);
    }
  };

  // Clic sur une citation : le message cité, au milieu, surligné un instant.
  const jump = async (messageId: string) => {
    atBottomRef.current = false;
    const found = await jumpToMessage(conversationId, messageId);
    if (!found) {
      EventBus.emit('ui:toast', { message: 'Ce message n’est plus disponible.', tone: 'info' });
      return;
    }
    requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector(`[data-msg-id="${CSS.escape(messageId)}"]`);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el?.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      setFlashId(messageId);
      setTimeout(() => setFlashId((id) => (id === messageId ? null : id)), 1600);
    });
  };

  const backToLatest = () => {
    atBottomRef.current = true;
    void openThread(conversationId);
  };

  const reply = (m: ThreadMessage) => {
    setReplyTarget(conversationId, m);
    setSelectedId(null);
    inputRef.current?.focus();
  };

  const autosize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };
  useLayoutEffect(autosize, [input]);

  const send = () => {
    const text = replaceFinalSmiley(input.trim());
    // Un fichier encore en route : on attend qu'il soit arrivé (bouton grisé).
    if ((!text && readyFiles === 0) || uploading) return;
    setInput('');
    atBottomRef.current = true;
    void sendMessage(conversationId, text);
  };

  const lastOwn = [...messages].reverse().find((m) => m.senderId === me && !m.deletedAt && !m.pending && !m.system);

  // Personnes qu'on peut citer : celles de la conversation (privé, groupe), tout l'annuaire sinon.
  const mentionables = (): MemberEntry[] => {
    const all = [...members.values()].filter((m) => m.userId !== me);
    return conv && (conv.kind === 'direct' || conv.kind === 'group') ? all.filter((m) => conv.memberIds.includes(m.userId)) : all;
  };
  const suggestions = mention ? matchMembers(mentionables(), mention.query) : [];
  const syncMention = () => {
    const el = inputRef.current;
    const next = el ? mentionQueryAt(el.value, el.selectionStart ?? el.value.length) : null;
    setMention((cur) => (cur?.start === next?.start && cur?.query === next?.query ? cur : next));
    if (next?.query !== mention?.query) setMentionIndex(0);
  };
  const pickMention = (m: MemberEntry) => {
    const el = inputRef.current;
    if (!mention || !el) return;
    const caret = el.selectionStart ?? input.length;
    const insert = `@${m.displayName} `;
    const next = (input.slice(0, mention.start) + insert + input.slice(caret)).slice(0, MESSAGE_MAX_LENGTH);
    onChange(next);
    setMention(null);
    requestAnimationFrame(() => {
      const pos = Math.min(mention.start + insert.length, next.length);
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setMentionIndex((i) => (i + step + suggestions.length) % suggestions.length);
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        pickMention(suggestions[Math.min(mentionIndex, suggestions.length - 1)]!);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMention(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); return; }
    if (e.key === 'Escape' && replyTarget) { e.preventDefault(); e.stopPropagation(); setReplyTarget(conversationId, null); return; }
    // ↑ dans une saisie vide : modifier son dernier message (comme Slack).
    if (e.key === 'ArrowUp' && !input && lastOwn) { e.preventDefault(); setEditingId(lastOwn.id); }
  };

  const attach = (files: FileList | File[] | null | undefined) => {
    const list = Array.from(files ?? []);
    if (list.length > 0 && canAttach) addDraftFiles(conversationId, list);
  };

  // Coller une image (capture d'écran) dans la saisie.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!canAttach || e.clipboardData.files.length === 0) return;
    e.preventDefault();
    attach(e.clipboardData.files);
  };

  const onDragOver = (e: DragEvent) => {
    if (!canAttach || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragging(true);
  };
  const onDrop = (e: DragEvent) => {
    if (!canAttach) return;
    e.preventDefault();
    setDragging(false);
    attach(e.dataTransfer.files);
  };

  // Emoji inséré à l'endroit du curseur (ou à la fin), curseur placé juste après.
  const insertEmoji = (char: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? input.length;
    const end = el?.selectionEnd ?? input.length;
    const next = (input.slice(0, start) + char + input.slice(end)).slice(0, MESSAGE_MAX_LENGTH);
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = Math.min(start + char.length, next.length);
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const onChange = (value: string) => {
    setInput(value);
    if ((conv?.kind === 'direct' || conv?.kind === 'group') && value && Date.now() - lastTypingRef.current > TYPING_SEND_EVERY_MS) {
      lastTypingRef.current = Date.now();
      notifyTyping(conversationId);
    }
  };

  const subtitle = conv?.kind === 'direct'
    ? (otherId && online.get(otherId) ? `En ligne · ${online.get(otherId)!.zoneName}` : 'Hors ligne : verra ton message à sa connexion')
    : conv?.kind === 'group' ? `${conv.memberIds.length} membre${conv.memberIds.length > 1 ? 's' : ''} · visible par eux seulement`
    : conv?.kind === 'profile' ? 'Visible par ce groupe et les admins'
    : "Visible par tout l'espace";

  // Statut de son message quand c'est le dernier du fil : envoyé, puis en privé
  // reçu (arrivé sur un appareil de l'autre) et vu (lu).
  const last = messages[messages.length - 1];
  let lastStatus: string | null = null;
  if (last && last.senderId === me && !last.deletedAt && !last.system && last.pending !== 'failed') {
    const direct = conv?.kind === 'direct';
    lastStatus = last.pending ? 'Envoi…'
      : direct && conv.otherLastReadAt && last.createdAt <= conv.otherLastReadAt ? 'Vu'
      : direct && conv.otherDeliveredAt && last.createdAt <= conv.otherDeliveredAt ? 'Reçu'
      : 'Envoyé';
  }

  let lastDay = '';
  let prev: ThreadMessage | null = null;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, position: 'relative' }}
      onDragOver={onDragOver} onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }} onDrop={onDrop}
    >
      {dragging && (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 8, zIndex: 5, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '2px dashed var(--color-primary)', borderRadius: 'var(--radius-lg)', background: 'var(--color-primary-muted)', color: 'var(--color-primary-text)', fontWeight: 700 }}>
          Déposer pour joindre au message
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--color-border-default)' }}>
        {onBack && <Button variant="ghost" size="sm" icon="arrow-left" aria-label="Tous les messages perso" onClick={onBack} />}
        {otherId && <PersonAvatar userId={otherId} name={title} size={32} />}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>{title}</span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>{subtitle}</span>
        </span>
        {conv?.kind === 'group' && onInfo && (
          <Button variant="ghost" size="sm" icon="users" aria-label="Infos du groupe" title="Membres, admins, quitter" onClick={onInfo} />
        )}
      </div>
      {status === 'offline' && (
        <p role="status" className="pl-help" style={{ margin: 0, padding: '6px 16px', background: 'var(--color-bg-raised)' }}>
          Connexion perdue. Nouvel essai en cours, tes messages partiront dès le retour.
        </p>
      )}
      <div ref={scrollRef} data-scroll-area onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', touchAction: 'pan-y', padding: '12px 16px 16px', display: 'flex', flexDirection: 'column' }}>
        {thread?.loadingOlder && <p className="pl-help" style={{ textAlign: 'center' }}>Chargement…</p>}
        {!thread || (thread.loading && messages.length === 0) ? (
          <p className="pl-help" style={{ margin: 'auto', textAlign: 'center' }}>Chargement…</p>
        ) : messages.length === 0 ? (
          <p className="pl-help" style={{ margin: 'auto', textAlign: 'center' }}>
            {conv?.kind === 'direct' || conv?.kind === 'group'
              ? `Pas encore de message. Écris à ${title} : personne d'autre ne verra cette conversation.`
              : "Aucun message pour l'instant. Dis bonjour à tout le monde !"}
          </p>
        ) : messages.map((m) => {
          const day = dayLabel(new Date(m.createdAt));
          const showDay = day !== lastDay;
          lastDay = day;
          if (m.system) {
            prev = null; // le message suivant reprend son en-tête
            return (
              <div key={m.id}>
                {showDay && <DayDivider day={day} />}
                <SystemLine message={m} />
              </div>
            );
          }
          const showHeader = showDay || !prev || prev.senderId !== m.senderId
            || new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() > GROUP_GAP_MS;
          prev = m;
          return (
            <div key={m.id}>
              {showDay && <DayDivider day={day} />}
              <MessageItem
                message={m}
                showHeader={showHeader}
                status={m.id === last?.id ? lastStatus : null}
                editing={editingId === m.id}
                onStartEdit={() => { setEditingId(m.id); setSelectedId(null); }}
                onStopEdit={() => { setEditingId(null); inputRef.current?.focus(); }}
                selected={selectedId === m.id}
                onSelect={() => setSelectedId((id) => (id === m.id ? null : m.id))}
                onReply={() => reply(m)}
                onJump={(id) => { void jump(id); }}
                flash={flashId === m.id}
              />
            </div>
          );
        })}
      </div>
      {thread?.newerCursor && (
        <div style={{ position: 'relative', height: 0 }}>
          <Button size="sm" variant="secondary" icon="chevron-down" onClick={backToLatest}
            style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', boxShadow: 'var(--shadow-md)', zIndex: 4 }}>
            Revenir aux derniers messages
          </Button>
        </div>
      )}
      <TypingLine conversationId={conversationId} />
      <DraftFiles conversationId={conversationId} />
      {replyTarget && <ReplyBanner conversationId={conversationId} target={replyTarget} />}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'flex-end', padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--color-border-default)' }}
      >
        <MentionSuggestions items={suggestions} active={Math.min(mentionIndex, suggestions.length - 1)}
          onPick={pickMention} onHover={setMentionIndex} />
        {recording ? (
          <VoiceRecorder conversationId={conversationId} onClose={() => { setRecording(false); atBottomRef.current = true; inputRef.current?.focus(); }} />
        ) : (<>
        {canAttach && (
          <input
            ref={fileRef} type="file" multiple accept={ATTACHMENT_ACCEPT} hidden
            onChange={(e) => { attach(e.target.files); e.target.value = ''; }}
          />
        )}
        <Button variant="ghost" icon="plus" aria-label="Ajouter" title="Fichier, emoji, GIF"
          aria-expanded={extra !== null} onClick={() => setExtra((v) => (v ? null : 'menu'))} style={{ height: 38, flexShrink: 0 }} />
        {extra === 'menu' && (
          <Menu label="Ajouter" onClose={() => setExtra(null)} sheet={isMobile}
            style={isMobile ? undefined : { position: 'absolute', left: 16, bottom: 'calc(100% + 4px)', minWidth: 220 }}>
            {canAttach && <MenuItem icon="paperclip" onSelect={() => { setExtra(null); fileRef.current?.click(); }}>Fichier ou photo</MenuItem>}
            <MenuItem icon="smile" onSelect={() => setExtra('emoji')}>Emoji</MenuItem>
            {gifsEnabled && <MenuItem icon="image" onSelect={() => setExtra('gif')}>GIF</MenuItem>}
          </Menu>
        )}
        {extra === 'emoji' && <EmojiPanel onPick={insertEmoji} onClose={() => setExtra(null)} />}
        {extra === 'gif' && <GifPanel conversationId={conversationId} onClose={() => setExtra(null)} onSent={() => { atBottomRef.current = true; inputRef.current?.focus(); }} />}
        <textarea
          ref={inputRef}
          className="pl-input pl-composer pl-grow"
          aria-label={`Message à ${title}`}
          rows={1}
          value={input}
          maxLength={MESSAGE_MAX_LENGTH}
          onChange={(e) => {
            // « :) » suivi d'un espace devient 🙂, curseur gardé à sa place.
            const el = e.target;
            const typed = replaceTypedSmileys(el.value, el.selectionStart ?? el.value.length);
            onChange(typed.text);
            if (typed.text !== el.value) requestAnimationFrame(() => el.setSelectionRange(typed.caret, typed.caret));
            syncMention();
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') syncMention(); }}
          onClick={syncMention}
          onBlur={() => setMention(null)}
          onPaste={onPaste}
          placeholder={conv?.kind === 'direct' ? `Écrire à ${title}…` : conv?.kind === 'profile' || conv?.kind === 'group' ? `Écrire au groupe ${title}…` : "Écrire à tout l'espace…"}
          autoFocus
        />
        {canAttach && canRecordVoice() && (
          <Button variant="ghost" icon="mic" aria-label="Enregistrer un message vocal" title="Message vocal (5 min au plus)"
            onClick={() => setRecording(true)} style={{ height: 38 }} />
        )}
        <Button variant="primary" type="submit" icon="send" aria-label="Envoyer"
          title={uploading ? 'Envoi des fichiers en cours…' : 'Envoyer (Entrée)'}
          disabled={(!input.trim() && readyFiles === 0) || uploading} style={{ height: 38 }} />
        </>)}
      </form>
    </div>
  );
}
